import { useState, useEffect, useRef } from "react";
import {
  Cookie,
  Shield,
  X,
  ChevronDown,
  ChevronUp,
  Settings,
  CheckCircle,
} from "lucide-react";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

const COOKIE_CONSENT_KEY = "cookie-consent-preferences";

const getCookiePreferences = (): CookiePreferences | null => {
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const setCookiePreferences = (prefs: CookiePreferences) => {
  localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
};

export default function CookieConsentBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const existing = getCookiePreferences();
    if (!existing) {
      const timer = setTimeout(() => {
        setShowBanner(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setAnimate(true));
        });
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (showBanner) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showBanner]);

  const dismiss = (prefs: CookiePreferences) => {
    setCookiePreferences(prefs);
    setAnimate(false);
    setTimeout(() => {
      setShowBanner(false);
      setShowSettings(false);
    }, 350);
  };

  const handleAcceptAll = () =>
    dismiss({
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    });

  const handleRejectAll = () =>
    dismiss({
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    });

  const handleSavePreferences = () => dismiss(preferences);

  const handleToggle = (key: keyof CookiePreferences) => {
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div
        aria-hidden="true"
        className={`fixed inset-0 z-[9998] transition-opacity duration-500 ${
          animate ? "opacity-100" : "opacity-0"
        }`}
        style={{
          background: "rgba(0,0,0,0.7)",
          backdropFilter: "blur(8px)",
        }}
      />

      {/* Modal */}
      <div
        ref={bannerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Cookie consent"
        className={`fixed inset-0 z-[9999] flex items-center justify-center p-3 transition-all duration-500 ${
          animate ? "opacity-100 scale-100" : "opacity-0 scale-95"
        }`}
      >
        <div className="relative w-full max-w-md">
          <div className="relative overflow-hidden bg-white shadow-2xl rounded-xl">
            {/* Close button */}
            <button
              onClick={() => dismiss(preferences)}
              className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-all z-10"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="p-4 md:p-6">
              {/* Header */}
              <div className="flex items-center gap-3 mb-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-full shadow-md bg-gradient-to-br from-orange-500 to-orange-600 shrink-0">
                  <Cookie className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">
                    We value your privacy
                  </h2>
                  <p className="text-xs text-gray-500">
                    We use cookies to enhance your experience
                  </p>
                </div>
              </div>

              {/* Content - Shortened */}
              <p className="mb-3 text-xs leading-relaxed text-gray-600">
                We use cookies to improve your browsing experience. By clicking
                "Accept All", you consent to our use of cookies.{" "}
                <a
                  href="/privacy"
                  className="font-medium text-orange-600 hover:underline"
                >
                  Learn more
                </a>
              </p>

              {/* Actions - Simplified */}
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <button
                    onClick={handleAcceptAll}
                    className="flex-1 py-2 text-xs font-semibold text-white transition-all rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md"
                  >
                    Accept All
                  </button>
                  <button
                    onClick={handleRejectAll}
                    className="flex-1 py-2 text-xs font-medium text-gray-700 transition-all bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Reject All
                  </button>
                </div>

                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="flex items-center justify-center gap-1 py-1.5 text-xs font-medium text-orange-600 hover:text-orange-700 transition-all"
                >
                  <Settings className="w-3.5 h-3.5" />
                  Customize
                  {showSettings ? (
                    <ChevronUp className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronDown className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>

              {/* Settings Panel - Compact */}
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  showSettings
                    ? "max-h-[500px] opacity-100 mt-3"
                    : "max-h-0 opacity-0"
                }`}
              >
                <div className="pt-3 border-t border-gray-200">
                  <p className="mb-2 text-xs font-medium text-gray-700">
                    Manage preferences
                  </p>
                  <div className="space-y-2">
                    {[
                      {
                        key: "necessary",
                        title: "Necessary",
                        disabled: true,
                      },
                      {
                        key: "analytics",
                        title: "Analytics",
                      },
                      {
                        key: "marketing",
                        title: "Marketing",
                      },
                      {
                        key: "preferences",
                        title: "Preferences",
                      },
                    ].map((item) => {
                      const isChecked =
                        preferences[item.key as keyof CookiePreferences];
                      return (
                        <div
                          key={item.key}
                          className="flex items-center justify-between py-1"
                        >
                          <span className="text-xs text-gray-700">
                            {item.title}
                          </span>
                          <button
                            onClick={() =>
                              !item.disabled &&
                              handleToggle(item.key as keyof CookiePreferences)
                            }
                            disabled={item.disabled}
                            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${
                              isChecked ? "bg-orange-600" : "bg-gray-300"
                            } ${item.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${
                                isChecked ? "translate-x-4" : "translate-x-0.5"
                              } top-0.5`}
                            />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    onClick={handleSavePreferences}
                    className="w-full py-2 mt-3 text-xs font-semibold text-white transition-all rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md"
                  >
                    Save Preferences
                  </button>
                </div>
              </div>

              {/* Footer */}
              <div className="pt-2 mt-3 text-center border-t border-gray-100">
                <p className="text-[10px] text-gray-400">
                  You can change preferences anytime in{" "}
                  <a
                    href="/privacy#cookies"
                    className="font-medium text-orange-600 hover:underline"
                  >
                    settings
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}